import type { QueryResultsPayload } from "../components/QueryResultsGrid.js";
import type { TanTableRenderModel } from "./tanTableSpec.js";
import type { TableColumnSpecV1 } from "./tableSpec.js";

function mapQueryTypeToDataType(t: string): TableColumnSpecV1["dataType"] {
  switch (t) {
    case "number":
      return "string";
    case "date":
      return "string";
    default:
      return "string";
  }
}

function renderModelColType(t: string): string {
  return t === "number" ? "number" : "string";
}

/** Builds a TanTable render model from a tenant / connector SQL query payload (column-major row arrays). */
export function queryResultsToTanTableModel(result: QueryResultsPayload): TanTableRenderModel {
  // SQL allows duplicate column names in a result set (e.g. SELECT a.*, b.*
  // where both share a column). The wire format preserves both via positional
  // arrays, but react-table requires unique ColumnDef ids. Suffix any
  // duplicate name with its column index so id/key/field are unique while
  // the user-facing label stays as-typed.
  const nameCounts = new Map<string, number>();
  for (const c of result.columns) nameCounts.set(c.name, (nameCounts.get(c.name) ?? 0) + 1);
  const uniqueKey = (name: string, index: number): string =>
    (nameCounts.get(name) ?? 0) > 1 ? `${name}__${index}` : name;

  const columns = result.columns.map((c, i) => ({
    key: uniqueKey(c.name, i),
    label: c.name,
    type: renderModelColType(c.type),
  }));

  const tableColumns: TableColumnSpecV1[] = result.columns.map((c, i) => ({
    id: uniqueKey(c.name, i),
    field: uniqueKey(c.name, i),
    label: c.name,
    dataType: mapQueryTypeToDataType(c.type),
    sortable: true,
    resizable: true,
  }));

  return {
    kind: "tantable",
    columns,
    rows: result.rows,
    rowCount: result.rowCount,
    tableSpec: {
      specVersion: "tantable.v1",
      kind: "tantable",
      columns: tableColumns,
      rowSelection: "none",
    },
  };
}
